import React, { useCallback, useEffect, useMemo } from "react";
import { usePatient } from "../state/patientContext";
import { useConversationDD } from "../solverHooks/useConversationDD";
import DiseaseView from "./uiControllers/DiseaseView";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import ResultHeader from "./uiControllers/ResultHeader";
import { PatientColorMap } from "../layout/patientColorMap";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AccordionDetails, styled, Theme } from "@mui/material";
import { makeStyles, createStyles } from "@mui/styles";
import { ActionButton, FlexCenterWrapper } from "./uiControllers/CommonStyled";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { usePatientRouteChanger } from "../PatientRoutes/routeChanger";
import { PatientRoute } from "../PatientRoutes/PatientRoute";
import { useTranslate } from "../hooks/useTranslate";
import { RouteContainer } from "../PatientRoutes/RouteContainer";
import {
  WaveResultInnerWrapper,
  WaveResultOuterWrapper,
} from "../style/useWaveStyles";
import { FeedbackContainer } from "./feedbackContainer";
import { useClinicData } from "../clinic/clinicHooks";
import { useWidgetSettings } from "../widget/useWidgetSettings";
import { useCalculateFindingPredictives } from "fe-common/utils/FindingStrengthCalculator";
import { INFOMED_PARTNER_ID } from "../widget/partnerIds";
import { PatientWhatsNext } from "./Results/patientWhatsNext";
import { usePatientFeatureFlags } from "../hooks/usePatientFeatureFlags";
import { ClinicCode } from "./uiControllers/clinicCode";
import clsx from "clsx";
import { NavigationRecommendationIndicator } from "fe-web-components/navigationRecommendation/navigationRecommendation";
import { AskDoctorAsButton } from "./Results/askDoctorAsButton";
import { scrollableStyleByColor } from "fe-common/ui/commonStyles";
import { ShowInChatReasoningButton } from "./demodebug/mobileSuggestionZoom/showInChatReasoningButton";
import {
  marginLeftToMarginRightOnRtl,
  onRtl,
} from "fe-web-components/rtl/rtlStyles";
import { useShowReviewPopup } from "../layout/useShowReviewPopup";

//region [[ Styles ]]
const ResultContainerView = styled("div")({
  position: "relative",
  minHeight: "100%",
  height: "100%",
  ...scrollableStyleByColor(PatientColorMap.Header),
  display: "flex",
  flexFlow: "column",
});

//region [[ Styles ]]
const UrgencyContainer = styled("div")({
  padding: "0 36px",
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    result: {
      padding: "20px 0",
      lineHeight: "20px",
    },
    resultText: {
      padding: "0 36px",
      whiteSpace: "break-spaces",
      lineHeight: "22px",
    },
    dd: {
      padding: "0 36px",
    },
    disclaimer: {
      position: "relative",
      margin: "0 36px 18px",
      padding: "8px 26px",
      border: ({ linkToExternalDisease }: { linkToExternalDisease: string }) =>
        linkToExternalDisease === "true"
          ? ""
          : `2px solid ${PatientColorMap.Background}`,
      boxShadow: ({
        linkToExternalDisease,
      }: {
        linkToExternalDisease: string;
      }) =>
        linkToExternalDisease === "true"
          ? "0px 0px 4px rgba(0, 0, 0, 0.14)"
          : "",
      borderRadius: "5px",
      color: ({ linkToExternalDisease }: { linkToExternalDisease: string }) =>
        linkToExternalDisease === "true" ? "" : PatientColorMap.Primary,
      fontSize: "14px",
    },
    shortCode: {
      position: "relative",
      margin: "0 36px 18px",
      padding: "20px 20px",
      borderRadius: "5px",
      backgroundColor: PatientColorMap.Unselected,
      color: "black",
      fontSize: "36px",
      fontWeight: "bolder",
      letterSpacing: "0.4em",
      textAlign: "center",
    },
    iconContainer: {
      position: "absolute",
      top: "-8px",
      left: "-8px",
      padding: "4px",
      backgroundColor: PatientColorMap.Background,
      borderRadius: "50%",
    },
    iconContainerPartner: {
      position: "absolute",
      top: "4px",
      left: "4px",
      margin: "4px",
      height: "23px",
      width: "23px",
      borderRadius: "50%",
      ...onRtl({
        left: 0,
        right: "4px",
      }),
    },
    disclaimerText: {
      ...marginLeftToMarginRightOnRtl(10),
    },
    clusterTitle: {
      color: PatientColorMap.Default,
      fontSize: "16px",
      fontWeight: "bold",
    },
    bottomOuterWrapper: {
      height: "50px",
      backgroundColor: PatientColorMap.Background,
    },
    bottomInnerWrapper: {
      height: "50px",
      backgroundColor: "white",
      borderRadius: "0 0 0 80px",
    },
    collapsed: {
      flexDirection: "column",
    },
  }),
);

const ResultTextTop = styled("p")(({ theme }: { theme?: Theme }) => ({
  fontWeight: "bold",
  fontSize: "14px",
}));

const ButtonsArea = styled(FlexCenterWrapper)({
  "& > *": {
    margin: "3px",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]
interface ResultContainerProps extends RouteContainer {}
//endregion [[ Props ]]

//region [[ Texts ]]
const MOST_LIKELY_TEXT = "MOST_LIKELY_TEXT";
const POSSIBLE_CAUSES_TEXT = "POSSIBLE_CAUSES_TEXT";
const GO_TO_SUMMARY_BUTTON_TEXT = "GO_TO_SUMMARY_BUTTON_TEXT";
const RESULT_MAIN_TEXT = "RESULT_MAIN_TEXT";

//endregion [[ Texts ]]

export const ResultContainer = ({
  embedded,
  ...props
}: ResultContainerProps) => {
  const { isClinicMode } = useClinicData();

  useShowReviewPopup();

  const resultsButton = usePatientFeatureFlags("resultsButton");
  const showCodeAtEndOfChat = usePatientFeatureFlags("showCodeAtEndOfChat");
  const showPossibleCauses = usePatientFeatureFlags("possibleCauses");

  const { translate } = useTranslate();

  const { partnerId } = useWidgetSettings();

  const { clinicSettings } = useClinicData();

  const linkToExternalDisease = useMemo(
    () =>
      partnerId === INFOMED_PARTNER_ID || clinicSettings?.linkToExternalDisease,
    [partnerId, clinicSettings],
  );

  const hideDD = clinicSettings.hideDD;

  const classes = useStyles({
    linkToExternalDisease: linkToExternalDisease ? "true" : "false",
  });

  const {
    chiefComplaint,
    conversationDDResult,
    shareId,
    caseId,
    gender,
    age,
    loading: conversationLoading,
    agreeOnTerms,
  } = usePatient();

  const ddClusters = conversationDDResult.ddClusters;

  const { loading: ddLoading } = useConversationDD();

  const getFindingsStrength = useCalculateFindingPredictives(
    conversationDDResult && conversationDDResult.crs.length > 0
      ? conversationDDResult.crs
      : null,
  );

  const loading = conversationLoading || ddLoading;

  const routeChanger = usePatientRouteChanger();

  const showAskDoctor = usePatientFeatureFlags("askDoctorAsButton");
  const isLocaleChangerFlagActive = usePatientFeatureFlags("localeChanger");
  const { name: clinicName, clinic: clinicId } = useClinicData();

  useEffect(() => {
    evt({
      category: CATEGORY.PATIENT_RESULT,
      action: ACTION.PATIENT_PAGE_LOAD,
      partnerId,
      props: {
        clinicName,
        clinicId,
      },
    });
  }, [partnerId, clinicName, clinicId]);

  const handleCausesClicked = useCallback(() => {
    evt({
      category: CATEGORY.PATIENT_RESULT,
      action: ACTION.PATIENT_BUTTON_CLICKED,
      label: "Possible causes",
      partnerId,
      props: {
        clinicName,
        clinicId,
      },
    });
  }, [partnerId, clinicName, clinicId]);

  const resultText1 = `${translate(RESULT_MAIN_TEXT)}`;

  const goToSummary = useCallback(() => {
    routeChanger(PatientRoute.Summary);
  }, [routeChanger]);

  if (
    !chiefComplaint &&
    !loading &&
    !shareId &&
    !caseId &&
    !embedded &&
    !agreeOnTerms
  ) {
    // routeWithQueryParam(history, "/"); // TBD is this right?
    routeChanger(PatientRoute.Questions);
    return null;
  }

  return (
    <ResultContainerView className={props.className}>
      <ResultHeader
        gender={gender}
        age={age?.toString() || ""}
        isLocaleChangerFlagActive={!!isLocaleChangerFlagActive}
      />
      <WaveResultOuterWrapper>
        <WaveResultInnerWrapper>
          <div className={classes.result}>
            {!loading && (
              <>
                <ResultTextTop className={classes.resultText}>
                  {resultText1}
                </ResultTextTop>
              </>
            )}
          </div>
        </WaveResultInnerWrapper>
      </WaveResultOuterWrapper>
      {(showCodeAtEndOfChat || isClinicMode) && <ClinicCode />}
      {loading || (!chiefComplaint && !caseId) ? (
        <ThreeDotsLoader color={PatientColorMap.Primary} />
      ) : (
        <>
          {ddClusters.top.length > 0 &&
            conversationDDResult.navigationRecommendation?.feature.feature && (
              <UrgencyContainer>
                <NavigationRecommendationIndicator
                  feature={
                    conversationDDResult.navigationRecommendation?.feature
                      .feature
                  }
                >
                  <ShowInChatReasoningButton
                    triage
                    isBackgroundSuggestion={false}
                  />
                </NavigationRecommendationIndicator>
              </UrgencyContainer>
            )}
          {showAskDoctor && (
            <AskDoctorAsButton
              feature={
                conversationDDResult.navigationRecommendation?.feature.feature
              }
            />
          )}

          {!hideDD && ddClusters.top.length > 0 && (
            <div className={classes.dd}>
              <div className={classes.clusterTitle}>
                <div className={classes.clusterTitle}>
                  {translate(MOST_LIKELY_TEXT)}
                </div>
              </div>
              {ddClusters.top.map((cluster) => {
                return (
                  <DiseaseView
                    key={cluster.id}
                    cluster={cluster}
                    getFindingsStrength={getFindingsStrength}
                  />
                );
              })}
              {showPossibleCauses && ddClusters.others.length > 0 && (
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    onClick={handleCausesClicked}
                  >
                    <div className={classes.clusterTitle}>
                      {translate(POSSIBLE_CAUSES_TEXT)}
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className={classes.collapsed}>
                    {ddClusters.others.map((cluster) => {
                      return (
                        <DiseaseView
                          key={cluster.id}
                          cluster={cluster}
                          getFindingsStrength={getFindingsStrength}
                        />
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              )}
              <FeedbackContainer />
            </div>
          )}
          <PatientWhatsNext />
          <div
            className={clsx(classes.bottomOuterWrapper, "bottom-outer-wrapper")}
          >
            <div className={classes.bottomInnerWrapper}> </div>
          </div>
          {/* Hide the summary link for when running with partner. */}
          {!partnerId && (
            <ButtonsArea>
              {resultsButton && (
                <ActionButton
                  onClick={goToSummary}
                  label={translate(GO_TO_SUMMARY_BUTTON_TEXT)}
                />
              )}
            </ButtonsArea>
          )}
        </>
      )}
    </ResultContainerView>
  );
};
